import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Globe, 
  Search, 
  Star, 
  Phone, 
  CheckCircle, 
  ArrowRight,
  Rocket,
  MessageSquare,
  Smartphone,
  Zap,
  X,
  Calendar,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BOOKING_URL = "https://api.forcesystems.io/widget/booking/MY9TsUsbO2gkyDu286JV";

const BrandPresence = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const navigate = useNavigate();

  // Load the booking script when modal opens
  useEffect(() => {
    if (showBookingModal) {
      const script = document.createElement('script');
      script.src = 'https://api.forcesystems.io/js/form_embed.js';
      script.type = 'text/javascript';
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [showBookingModal]);

  const features = [
    {
      icon: Globe,
      title: 'Professional Website Design',
      description: 'Custom-built, mobile-optimized website that converts visitors into leads. Showcases your work and builds trust.'
    },
    {
      icon: Search,
      title: 'Local SEO Optimization',
      description: 'Rank higher on Google for roofing searches in your area. Stop paying for unqualified leads from directories.'
    },
    {
      icon: Star,
      title: '5-Star Review Funnel',
      description: 'Automated system to collect and showcase your best reviews. Filter negative reviews before they go public.'
    },
    {
      icon: Phone,
      title: 'Missed Call Text-Back',
      description: 'Never lose a lead. Automatic text response when you miss a call, keeping the conversation going.'
    },
    {
      icon: MessageSquare,
      title: 'Google Business Optimization',
      description: 'Fully optimized Google Business Profile to dominate local search and maps results.'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Design',
      description: '87% of customers view your site on mobile. We ensure a perfect experience on every device.'
    }
  ];

  const stats = [
    { value: '154%', label: 'Average Client Growth' },
    { value: '5000+', label: 'Leads Generated' },
    { value: '127+', label: 'Projects Completed' },
    { value: '112+', label: '5-Star Reviews' }
  ];

  const testimonials = [
    {
      quote: "The website they built looks incredible, and the systems they put in place have made handling leads effortless.",
      author: "Mark",
      company: "Summit Exterior Washing"
    },
    {
      quote: "I've been blown away by the quality of work. My online presence looks better than ever, and I'm already seeing more inbound leads.",
      author: "Daniel",
      company: "BrightLine Electrical"
    },
    {
      quote: "Professional, responsive, and delivered exactly what was promised. A game-changer for my business.",
      author: "Jake",
      company: "Precision Paint Co"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <div className="text-2xl font-bold text-white">
            HomeFixer360
          </div>
          <Button 
            onClick={() => navigate('/login')}
            variant="outline"
            className="border-purple-400 text-purple-300 hover:bg-purple-500/20"
          >
            Login
          </Button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-16">
        {/* Hero Section */}
        <div className="text-center space-y-8">
          <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-400/30 rounded-full px-6 py-2">
            <Rocket className="w-5 h-5 text-purple-400" />
            <span className="text-purple-300 font-semibold">Premium Add-On Service</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
            Dominate Your Local Market With{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
              Online Brand Presence
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Let us build and manage your complete digital presence—so you can focus on your trade while leads come to you.
          </p>
          
          {/* Pricing */}
          <div className="flex flex-wrap justify-center gap-6 py-8">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl px-8 py-6 text-center">
              <p className="text-4xl font-bold text-white">$1,000</p>
              <p className="text-purple-300">One-Time Setup</p>
            </div>
            <div className="text-slate-500 self-center text-2xl">+</div>
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl px-8 py-6 text-center">
              <p className="text-4xl font-bold text-white">$400<span className="text-lg text-slate-400">/mo</span></p>
              <p className="text-purple-300">Ongoing Management</p>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowBookingModal(true)}
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold px-10 py-7 text-lg rounded-xl shadow-lg shadow-purple-500/25"
          >
            <Calendar className="w-6 h-6 mr-3" />
            Book Your Free Consultation
            <ArrowRight className="w-6 h-6 ml-3" />
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">{stat.value}</p>
              <p className="text-slate-400 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* What's Included */}
        <div className="space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white flex items-center justify-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-400" />
              What's Included
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-purple-500/50 transition-colors"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-white text-lg mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-white text-center mb-10 flex items-center justify-center gap-3">
            <Zap className="w-8 h-8 text-yellow-400" />
            Simple 3-Step Process
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
              <h3 className="font-semibold text-white text-xl mb-2">Demo Call (20 min)</h3>
              <p className="text-slate-400">We show you live client accounts and answer all your questions.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
              <h3 className="font-semibold text-white text-xl mb-2">We Build (7-10 days)</h3>
              <p className="text-slate-400">Full website build out with all systems customized for your business.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
              <h3 className="font-semibold text-white text-xl mb-2">Launch (30 min)</h3>
              <p className="text-slate-400">We walk you through everything and you can make unlimited changes.</p>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="space-y-8">
          <h2 className="text-3xl font-bold text-white text-center flex items-center justify-center gap-3">
            <Star className="w-8 h-8 text-yellow-400" />
            What Contractors Say
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 italic mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-white">{testimonial.author}</p>
                  <p className="text-slate-500 text-sm">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Grow Your Home Service Business?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto text-lg">
            Join hundreds of contractors who have transformed their online presence. 
            Get started today with a free consultation call.
          </p>
          <Button 
            onClick={() => setShowBookingModal(true)}
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold px-10 py-7 text-lg rounded-xl shadow-lg shadow-purple-500/25"
          >
            <Calendar className="w-6 h-6 mr-3" />
            Schedule Your Free Consultation
            <ArrowRight className="w-6 h-6 ml-3" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-16 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-slate-500">
          <p>&copy; {new Date().getFullYear()} HomeFixer360. All rights reserved.</p>
        </div>
      </footer>

      {/* Booking Calendar Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">Book Your Free Consultation</h2>
              </div>
              <button 
                onClick={() => setShowBookingModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="p-4" style={{ height: '600px' }}>
              <iframe 
                src={BOOKING_URL}
                style={{ width: '100%', height: '100%', border: 'none', overflow: 'hidden' }}
                scrolling="no"
                title="Book a Consultation"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandPresence;
